/**
 * Which tab of a hosted screen is showing, read from the URL rather than from component state.
 *
 * The URL, because three things depend on it being there. The redirect table sends `/dictionaries` to
 * `/policies?tab=dictionaries`, so the tab has to be addressable. The end-to-end suite navigates by
 * URL throughout and would otherwise have to click its way to a screen it used to `goto`. And an
 * operator who sends a colleague a link to the Trust tab should be sending them the Trust tab.
 *
 * An unrecognized or absent value resolves to the fallback rather than rendering nothing. A typo in a
 * pasted URL is common and shows the screen's main tab; a blank pane with working navigation around
 * it reads as a broken deployment.
 */
export function tabFor(search: string, allowed: readonly string[], fallback: string): string {
  const requested = new URLSearchParams(search).get('tab')
  return requested !== null && allowed.includes(requested) ? requested : fallback
}

/** A search string with one tab selected, preserving everything else already in the query. */
export function withTab(search: string, tab: string): string {
  const parameters = new URLSearchParams(search)
  parameters.set('tab', tab)
  return `?${parameters.toString()}`
}

/**
 * The same, for any set of keys at once, where a null removes one.
 *
 * `withTab` can only add, and a screen that holds a filter, a page and a selected row in the query
 * needs to clear them together - changing tab with a row open must not carry `selected` onto the tab
 * that knows nothing about it. Written as one call rather than three so those never come apart, and
 * returning `''` rather than `'?'` for an empty result so a bare path stays bare (D-441).
 */
export function withParams(search: string, changes: Record<string, string | null>): string {
  const parameters = new URLSearchParams(search)
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) parameters.delete(key)
    else parameters.set(key, value)
  }
  const query = parameters.toString()
  return query.length > 0 ? `?${query}` : ''
}

/** One value out of the query, or null. The counterpart of `withParams` for reading. */
export function paramFrom(search: string, key: string): string | null {
  return new URLSearchParams(search).get(key)
}
